import type { FC } from 'react';
import { useRouter } from 'next/router';

import { DEFAULT_VEHICLES_PAGE_SIZE, TEXT_FALLBACK } from 'Components/Account/AccountPage.consts';
import { onSortHandler, parseOrder, parseSortBy } from 'Components/Account/AccountPage.helpers';
import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import useAccountRouter from 'Components/Account/useAccountRouter';
import BidCreatedAtTimer from 'Components/Account/utilities/BidsCreatedAtTimer/BidsCreatedAtTimer';
import { VehicleListStateHandler } from 'Components/Account/utilities/VehicleListStateHandler/VehicleListStateHandler';
import cypressIds from 'Components/cypress_ids.json';
import { VehicleTradeBadge } from 'Components/shared/VehicleTradeBadge/VehicleTradeBadge';
import { triggerAccountVehicleDetailsClickedEvent } from 'Services/analytics/events';
import Datagrid, { DatagridBody, DatagridCell, DatagridHeader, DatagridRow } from 'Storybook/core/Datagrid';
import VehicleData from 'Storybook/peripheral/VehicleData/VehicleData';
import { Order } from 'Types/generic';
import { applyCypressData } from 'Utilities/helpers';
import ROUTES from 'Utilities/routes';

import { BidsAndOffersLoadingSkeleton } from './BidsAndOffersLoadingSkeleton/BidsAndOffersLoadingSkeleton';
import BidOfferCell from './components/BidOfferCell/BidOfferCell';
import { StatusTagGroup } from './components/StatusTagGroup/StatusTagGroup';
import { getBidsAndOffersListColumns } from './BidsAndOffersVehicleList.helpers';

import styles from './BidsAndOffersVehicleList.module.scss';

type BidsAndOffersVehicleListProps = VehicleListQuery;

const BidsAndOffersVehicleList: FC<BidsAndOffersVehicleListProps> = ({ data, isPreviousData, queryStatus }) => {
	const router = useRouter();
	const { query } = router;
	const { slug } = useAccountRouter();

	const columns = getBidsAndOffersListColumns({
		slug,
		sorts: { order: parseOrder(query, Order.Descending), sortBy: parseSortBy(query) },
	});

	const rowsCount = data?.results ? data.results.length : 0;

	return (
		<div className={styles.table}>
			<Datagrid colCount={columns.length} rowCount={rowsCount}>
				<DatagridHeader
					columns={columns}
					containerClassName={styles.datagridHeader}
					data-slug={slug}
					gridClass={styles.datagridCustom}
					onSortHandler={(id: string, newOrder: string) => onSortHandler(id, newOrder, router)}
				/>
				<DatagridBody {...applyCypressData(cypressIds.lists.accountVehicle)}>
					<VehicleListStateHandler
						isPreviousData={isPreviousData}
						placeholderRowsCount={DEFAULT_VEHICLES_PAGE_SIZE}
						queryStatus={queryStatus}
						rowsCount={data?.results?.length}
						skeletonRow={BidsAndOffersLoadingSkeleton}
					>
						{data?.results?.map(
							({ id, isTrade, leadPhoto, make, model, prettyVrm, soldFor, state, userBid }, index) => (
								<DatagridRow
									key={id}
									isInteractive
									as={ROUTES.VEHICLE.as(id)}
									cypressId={`${cypressIds.accountDataGrid.dataGridRow}-${index}`}
									gridClass={styles.datagridCustom}
									href={`${ROUTES.VEHICLE.href}?vehicleId=${id}`}
									id={id.toString()}
									index={index}
									onClick={() => {
										triggerAccountVehicleDetailsClickedEvent({ id, slug });
									}}
								>
									<DatagridCell index={0} rowIndex={index}>
										<VehicleData
											badges={isTrade ? [<VehicleTradeBadge key="tradeBadge" />] : []}
											cypressId={`${cypressIds.accountDataGrid.dataGridFields.vehicle}-${index}`}
											leadPhoto={leadPhoto}
											make={make}
											model={model}
											prettyVrm={prettyVrm}
										/>
									</DatagridCell>
									<DatagridCell index={1} rowIndex={index}>
										<StatusTagGroup
											cypressId={`${cypressIds.accountDataGrid.dataGridFields.status}-${index}`}
											soldFor={soldFor}
											state={state}
											userBid={userBid}
										/>
									</DatagridCell>
									<DatagridCell index={2} rowIndex={index}>
										<div className={styles.textContainer}>
											<BidOfferCell
												cypressId={`${cypressIds.accountDataGrid.dataGridFields.bidOffer}-${index}`}
												userBid={userBid}
											/>
										</div>
									</DatagridCell>
									<DatagridCell index={3} rowIndex={index}>
										<div
											className={styles.textContainer}
											{...applyCypressData(`${cypressIds.accountDataGrid.dataGridFields.bid}-${index}`)}
										>
											{userBid.createdAt ? <BidCreatedAtTimer value={String(userBid.createdAt)} /> : TEXT_FALLBACK}
										</div>
									</DatagridCell>
								</DatagridRow>
							),
						)}
					</VehicleListStateHandler>
				</DatagridBody>
			</Datagrid>
		</div>
	);
};

export { BidsAndOffersVehicleList };
